import { useEffect, useState } from 'react';
import { Avatar } from '@library/components/avatar/Avatar';
import { Button } from '@library/components/buttons';
import type { GetChannelDetailsResponse } from '@library/types/alerts';
import { Modal } from '@library/components/modals/Modal';
import { Spinner } from '@library/components/Spinner';
import { useAlertNotifications } from '@bo-hooks/alerts/useAlertNotifications';
import { useGetAdminCurrentQuery } from '@bo-services/authenticationApi';

export interface ChannelUsersModalProps {
  alertId: string;
  channelDetails: GetChannelDetailsResponse | undefined;
  isOpen: boolean;
  selectedChannelId: number;
  setIsError: (isError: boolean) => void;
  setIsSuccess: (isSuccess: boolean) => void;
  onCancelModal: () => void;
}

export const ChannelUsersModal = ({
  alertId,
  channelDetails,
  isOpen,
  selectedChannelId,
  setIsError,
  setIsSuccess,
  onCancelModal
}: ChannelUsersModalProps) => {
  const { data: endpointData } = useGetAdminCurrentQuery();

  const { handleTagUser, tagUserError, tagUserSuccess } =
    useAlertNotifications();

  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (tagUserSuccess) {
      setIsSuccess(true);
    }
  }, [tagUserSuccess, setIsSuccess]);

  useEffect(() => {
    if (tagUserError) {
      setIsError(true);
    }
  }, [tagUserError, setIsError]);

  if (!endpointData) {
    return null;
  }
  const { data } = endpointData;
  const userId = data.id;

  return (
    <Modal isOpen={isOpen} onCancelModal={onCancelModal}>
      <div className="bg-soFresh-100 flex min-h-52 w-96 flex-col">
        {!isFetching && (
          <p className="mx-3 my-2 text-xl">Select a user to tag</p>
        )}
        {isFetching && (
          <span className="m-auto flex flex-col items-center justify-center">
            <Spinner className="m-auto self-center" />
            <p>Tagging User</p>
          </span>
        )}
        {!channelDetails?.included && (
          <p className="text-base">
            There are no users subscribed to this channel
          </p>
        )}
        {channelDetails?.included &&
          channelDetails?.included.reduce<JSX.Element[]>((acc, channelUser) => {
            if (channelUser.id === userId) return acc;

            acc.push(
              <Button
                key={`user ${channelUser.id}`}
                onClick={() =>
                  handleTagUser(
                    channelUser.id,
                    alertId,
                    selectedChannelId,
                    setIsFetching,
                    onCancelModal
                  )
                }
                disabled={isFetching}
              >
                {!isFetching && (
                  <div className="border-soFresh-140 mx-3 my-2 flex items-center border-b p-2">
                    <Avatar />
                    <p className="ml-3">
                      {`@${channelUser.attributes.first_name} ${channelUser.attributes.last_name}`}
                    </p>
                  </div>
                )}
              </Button>
            );

            return acc;
          }, [])}
      </div>
    </Modal>
  );
};
